// src/components/main/HarvestWaitlist.js
import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  useTheme,
  CircularProgress,
} from '@mui/material';

export default function HarvestWaitlist({ harvestYear }) {
  const theme = useTheme();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL || ''}/waitlist`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name, email, harvestYear }),
        }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Sign‑up failed');
      }
      setSubmitted(true);
    } catch (err) {
      console.error('Waitlist error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      id="harvest-waitlist"
      sx={{
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
        py: { xs: 6, md: 10 },
      }}
    >
      <Container maxWidth="sm" sx={{ textAlign: 'center' }}>
        {/* Header graphic */}
        <Box
          component="img"
          src="https://brokepiggytoken.xyz/wp-content/uploads/2025/07/from-marrakech-with-heritage-8.png"
          alt="Harvest Waitlist"
          sx={{ width: { xs: 120, md: 160 }, mb: 3 }}
        />

        {/* Accent bar */}
        <Box
          sx={{
            width: 60,
            height: 4,
            bgcolor: theme.palette.secondary.main,
            mb: 2,
            borderRadius: 2,
            mx: 'auto',
          }}
        />

        {/* Title */}
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
          Join the Harvest {harvestYear} Waitlist
        </Typography>

        {/* Description */}
        <Typography variant="body1" sx={{ fontSize: '1rem', lineHeight: 1.6, mb: 4 }}>
          Sign up now to reserve your allocation from the {harvestYear} harvest.
          Be the first to know when bottles become available—exclusive for our waitlist members.
        </Typography>

        {submitted ? (
          <Typography variant="h6" sx={{ mt: 4, color: theme.palette.primary.main }}>
            Thank you! We’ll be in touch when Harvest {harvestYear} is ready.
          </Typography>
        ) : (
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            {error && (
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            )}
            <TextField
              label="Name"
              variant="outlined"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <TextField
              label="Email"
              variant="outlined"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Button
              type="submit"
              variant="contained"
              color="secondary"
              sx={{ mt: 2, py: 1.5, position: 'relative' }}
              disabled={loading}
            >
              {loading && (
                <CircularProgress
                  size={20}
                  sx={{
                    color: 'common.white',
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                  }}
                />
              )}
              {!loading && 'Join the Waitlist'}
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
}
